import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { encodeSlides, LvcPrimarySliderItem } from '../../modules/sliderUtils'
import { getSlides } from '../sliders/functions'
import { isMobile, mapEvent } from '../../modules/utils'
import { LvcItemSourceData } from '../../../types/global'
import { Locale } from '../../modules/locale'
import { UrlSearchCore } from '../../modules/search'

Locale.getInstance().registerKeySet('fr', 'lvc-activity-pack', {
  viewMore: 'voir tous les événements {{title}}',
})
Locale.getInstance().registerKeySet('en', 'lvc-activity-pack', {
  viewMore: 'see all {{title}} events',
})

export interface LvcActivityPackLayout {
  title: string
  titleHtmlTag: 'h2' | 'h3' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | null
  description?: string
  descriptionBanner?: string
  items: string
  total: number
  searchConfig?: string
  disableViewMore?: boolean
  disableLimit?: boolean
  layout?: 'grid' | 'slider'
  disableTitle: boolean
  parsedItems: LvcPrimarySliderItem[]
  dataComponentLocation: string
  limit: number
}
export class LvcActivityPackLayout extends MinzeElement {
  attrs: Attrs = [
    ['layout', 'grid'],
    'title',
    ['title-html-tag', 'h2'],
    'description',
    'description-banner',
    'items',
    'total',
    'search-config',
    ['disable-view-more', false],
    ['disable-limit', false],
    ['enable-title-on-desktop-view', false],
    ['data-component-location', 'activity_pack'],
    ['limit', 14],
  ]
  reactive: Reactive = [['isMobile', isMobile()]]
  handleWindowResize = () => {
    this.isMobile = isMobile()
  }
  onReady = () => {
    this.parsedItems = (JSON.parse(decodeURIComponent(this.items)) as LvcItemSourceData[]).map(
      item => {
        return mapEvent(item)
      }
    )
    this.rerender()
  }

  handleViewMoreOnClick = async () => {
    if (this.searchConfig) {
      const core = new UrlSearchCore('activity-pack') // Reset the search config
      const decoded = decodeURIComponent(this.searchConfig)
      await core.applySearchConfig(JSON.parse(decoded))
      core.goToSearch(true)
    }
  }
  renderDesktop = () => {
    return /*html*/ `
      ${this.description ? /*html*/ `<div class="lvc-activity-pack__description">${this.description}</div>` : ''}
      ${this.descriptionBanner ? /*html*/ `<img class="lvc-activity-pack__description-banner" src="${JSON.parse(decodeURIComponent(this.descriptionBanner)).desktop}"/>` : ''}
      <lvc-grid-layout
        data-component-location="${this.dataComponentLocation}"
        data="${encodeSlides(this.parsedItems.filter(item => item).slice(0, this.disableLimit ? this.parsedItems.length : this.limit))}"
      >
      </lvc-grid-layout>
      ${
        this.disableViewMore
          ? ''
          : /*html*/ `
        <div class="view-more-layout">
          <div id="view-more">
            <div class="view-more-text">${Locale.getKey('lvc-activity-pack.viewMore', {
              title: this.title,
            })}
            </div>
            <div class="view-more-icon-wrapper">
              <lvc-arrow-right-icon width="35px" height="35px"></lvc-arrow-right-icon>
            </div>
          </div>
        </div>
      `
      }
    `
  }

  renderMobile = () => {
    return /*html*/ `
    <lvc-primary-slider
      list-name="${this.title}"
      items-per-slide="3"
      fade-effect="true"
      data-component-location="${this.dataComponentLocation}"
      data-description-banner="${this.descriptionBanner ? JSON.parse(decodeURIComponent(this.descriptionBanner)).mobile : null}"
      data-slides="
        ${encodeSlides(
          getSlides(
            this.parsedItems.filter(item => item),
            this.total,
            'lvc:activity-pack-view-more',
            this?.disableViewMore
          )
        )}"
      style="margin-bottom: 1rem;"
    ></lvc-primary-slider>
    `
  }

  html = () => /*html*/ `
      ${
        this.parsedItems && this.parsedItems.length > 0
          ? `
        ${
          this.enableTitleOnDesktopView
            ? /*html*/ `
        <div class="grid-title-wrapper">
          <${this.titleHtmlTag || 'h2'} class="grid-title">${this.title}</${this.titleHtmlTag || 'h2'}>
          <div class="grid-counter">${this.total}</div>
        </div>
      `
            : ''
        }
        <lvc-slider-title text="${this.title}" title-html-tag="${this.titleHtmlTag || 'h2'}" description="${this.description}" counter="${this.total}"></lvc-slider-title>
        ${this.isMobile || this.layout === 'slider' ? this.renderMobile() : this.renderDesktop()}
      `
          : '<lvc-no-results></lvc-no-results>'
      }
    `
  css = () => `
    :host {
    }
    /* @media screen and (min-width: 600px) {
      lvc-primary-slider {
        height: 450px;
      }
    } */
    .lvc-activity-pack__description {
      font-size: 14px;
    }
    .lvc-activity-pack__description-banner {
      width: 100%;
      height: auto;
      margin-bottom: 20px;
    }
    lvc-no-results {
      display: ${this.isMobile ? 'none' : 'flex'};
    }
    lvc-grid-layout {
      display: ${!this.layout || this.layout === 'grid' ? 'grid' : 'none'};
    }
    lvc-primary-slider {
      display: ${this.layout === 'slider' ? 'block' : 'none'};
    }
    lvc-slider-title {
      display: ${this.isMobile ? 'flex' : 'none'};
      margin-bottom: 16px;
    }
    .grid-title-wrapper {
      display: ${this.enableTitleOnDesktopView ? 'flex' : 'none'};
      align-items: center;
      gap: 8px;
    }
    .grid-title {
      font-size: 2rem;
      font-weight: 800;
      color: #1E007D;
      text-transform: uppercase;
      margin: 0;
    }
    .grid-counter {
      font-size: 1rem;
      margin-top: -25px;
    }
    .view-more-layout {
      display: ${this.disableViewMore ? 'none' : 'flex'};
      margin: auto;
      justify-content: center;
      margin-top: 60px;
    }
    #view-more {
      font-size: 1.5rem;
      font-weight: 500;
      color: #1E007D;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      gap: 20px;
      cursor: pointer;
    }
    .view-more-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 72px;
      height: 72px;
      background-color: #ffffff;
      border-radius: 100%;
    }
    lvc-arrow-right-icon {
      width: 35px;
      height: 35px;
      transform: rotate(-25deg);
    }
    .view-more-text {
      border-bottom: 1px solid #1E007D;
    }
    .title-container {
      display: ${!this.layout || this.layout === 'grid' ? 'none' : 'flex'};
      align-items: center;
      column-gap: 10px;
      cursor: pointer;
    }

    .counter {
      display: inline-flex;
      height: 34px;
      min-width: 34px;
      padding: 7px;
      justify-content: center;
      align-items: center;
      border-radius: 40px;
      background: #1E007D;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%;
      color: #FF4600;
      position: relative;
      top: 2px;
    }
    @media only screen and (max-width: 991px) {
      .grid-title-wrapper {
        display: none;
      }
      lvc-grid-layout {
        display: none;
      }
      lvc-primary-slider {
        display: block;
      }
      .title-container {
        display: flex;
      }
      .view-more-layout {
        display: none;
      }
    }
    @media only screen and (max-width: 767px) {
      :host {
        margin-left: 1rem;
        margin-right: 1rem;
      }
    }
  
`
  eventListeners: EventListeners = [
    [window, 'lvc:activity-pack-view-more', e => this.handleViewMoreOnClick()],
    ['#view-more', 'click', this.handleViewMoreOnClick],
    [window, 'resize', this.handleWindowResize],
  ]
}
